import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Flag, ExternalLink, Check, X, Trash2, Gem, Sparkles, ArrowUpCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { reportsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { cn, formatDate } from '../lib/utils';

const CATEGORY_ICONS = {
    relics: Gem,
    gachas: Sparkles,
    upgrades: ArrowUpCircle,
};

const CATEGORY_COLORS = {
    relics: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', border: 'border-cyan-500/30' },
    gachas: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/30' },
    upgrades: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/30' },
};

const STATUS_COLORS = {
    open: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30' },
    resolved: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/30' },
    dismissed: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/30' },
};

export default function ReportsPage() {
    const { isAdmin } = useAuth();
    const navigate = useNavigate();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');

    useEffect(() => {
        if (!isAdmin) {
            navigate('/');
            return;
        }
        loadReports();
    }, [isAdmin, navigate]);

    const loadReports = async () => {
        setLoading(true);
        try {
            const response = await reportsApi.getAll();
            setReports(response.data);
        } catch (error) {
            console.error('Failed to load reports:', error);
            toast.error('Failed to load reports');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (reportId, newStatus) => {
        try {
            await reportsApi.updateStatus(reportId, newStatus);
            toast.success('Status updated');
            loadReports();
        } catch (error) {
            console.error('Failed to update status:', error);
            toast.error('Failed to update status');
        }
    };

    const handleDelete = async (reportId) => {
        try {
            await reportsApi.delete(reportId);
            toast.success('Report deleted');
            loadReports();
        } catch (error) {
            console.error('Failed to delete report:', error);
            toast.error('Failed to delete report');
        }
    };

    const filteredReports = filterStatus === 'all' 
        ? reports 
        : reports.filter(r => r.status === filterStatus);

    const openCount = reports.filter(r => r.status === 'open').length;

    return (
        <div className="space-y-8" data-testid="reports-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="space-y-1">
                    <div className="flex items-center gap-3">
                        <h1 className="text-3xl font-bold font-['Rajdhani'] tracking-wider text-white">
                            <span className="text-red-400">Reports</span>
                        </h1>
                        {openCount > 0 && (
                            <span className="px-3 py-1 rounded-full bg-red-500/10 text-red-400 text-sm font-mono border border-red-500/30">
                                {openCount} open
                            </span>
                        )}
                    </div>
                    <p className="text-gray-400">
                        Review and manage user reports about incorrect values
                    </p>
                </div>
            </div>

            {/* Filter */}
            <div className="flex items-center gap-4">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-48 bg-black/40 border-white/10">
                        <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                        <SelectItem value="all">All Reports</SelectItem>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="dismissed">Dismissed</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Reports List */}
            <div className="space-y-4">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">Loading...</div>
                ) : filteredReports.length === 0 ? (
                    <div className="p-12 rounded-xl bg-card border border-white/5 text-center">
                        <Flag className="mx-auto mb-4 text-gray-600" size={48} />
                        <p className="text-gray-500">No reports found</p>
                    </div>
                ) : (
                    filteredReports.map((report) => {
                        const CategoryIcon = CATEGORY_ICONS[report.category] || Flag;
                        const categoryColors = CATEGORY_COLORS[report.category] || CATEGORY_COLORS.relics;
                        const statusColors = STATUS_COLORS[report.status] || STATUS_COLORS.open;

                        return (
                            <div
                                key={report.id}
                                data-testid={`report-${report.id}`}
                                className="p-6 rounded-xl bg-card border border-white/5 hover:border-white/10 transition-colors"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        {/* Header row with tags */}
                                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                                            {/* Category Tag */}
                                            <span className={cn(
                                                "inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium border",
                                                categoryColors.bg,
                                                categoryColors.text,
                                                categoryColors.border
                                            )}>
                                                <CategoryIcon size={12} />
                                                {report.category.charAt(0).toUpperCase() + report.category.slice(1)}
                                            </span>

                                            {/* Status Tag */}
                                            <span className={cn(
                                                "inline-flex items-center px-2.5 py-1 rounded text-xs font-medium border uppercase",
                                                statusColors.bg,
                                                statusColors.text,
                                                statusColors.border
                                            )}>
                                                {report.status}
                                            </span>

                                            {/* Date */}
                                            <span className="text-xs text-gray-500 font-mono ml-auto">
                                                {formatDate(report.created_at)}
                                            </span>
                                        </div>

                                        {/* Entry Link */}
                                        <Link 
                                            to={`/${report.category}/${report.entry_id}`}
                                            className="inline-flex items-center gap-2 text-white hover:text-cyan-400 transition-colors group mb-2"
                                        >
                                            <h3 className="font-['Rajdhani'] font-semibold text-lg">
                                                {report.entry_title}
                                            </h3>
                                            <ExternalLink size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </Link>

                                        {/* Report Message */}
                                        <p className="text-gray-400 whitespace-pre-line mb-3">
                                            {report.message}
                                        </p>

                                        {/* Reporter */}
                                        <p className="text-xs text-gray-600">
                                            Reported by: <span className="text-gray-400">{report.reporter_name}</span>
                                        </p>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-2 shrink-0">
                                        {report.status === 'open' && (
                                            <>
                                                <Button
                                                    data-testid={`resolve-${report.id}`}
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleStatusChange(report.id, 'resolved')}
                                                    className="text-gray-400 hover:text-green-400 hover:bg-green-500/10"
                                                    title="Mark as resolved"
                                                >
                                                    <Check size={18} />
                                                </Button>
                                                <Button
                                                    data-testid={`dismiss-${report.id}`}
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleStatusChange(report.id, 'dismissed')}
                                                    className="text-gray-400 hover:text-gray-300 hover:bg-gray-500/10"
                                                    title="Dismiss"
                                                >
                                                    <X size={18} />
                                                </Button>
                                            </>
                                        )}
                                        <Button
                                            data-testid={`delete-report-${report.id}`}
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDelete(report.id)}
                                            className="text-gray-400 hover:text-red-400 hover:bg-red-500/10"
                                            title="Delete"
                                        >
                                            <Trash2 size={18} />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
